import { animated, config, useSpring } from '@react-spring/web';
import { useFirstMountState } from 'react-use';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';

import { Score } from 'components/collections/CollectionEvent/Score';

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
`;

const ProgressLabel = styled(animated.small)`
  position: relative;
  color: var(--c-nd-600);
  top: 0;
  font-size: var(--intermediate-unit);
  line-height: 1;
  align-self: flex-start;
`;

type Props = {
  value: number;
  displayedValue: number;
  min?: number;
  max: number;
  skipStartAnimation?: boolean;
  style?: React.CSSProperties;
};

export const ProgressBar = ({
  value: toValue,
  displayedValue: toDisplayedValue,
  min = 0,
  max,
  skipStartAnimation = false,
  style,
}: Props) => {
  const firstRender = useFirstMountState();
  const { value } = useSpring({
    config: config.molasses,
    from: { value: min },
    to: { value: Math.min(Math.max(toValue, min), max) },
    immediate: skipStartAnimation && firstRender,
  });
  const { value: displayedValue } = useSpring({
    config: config.molasses,
    from: { value: min },
    to: { value: Math.max(toDisplayedValue, min) },
    immediate: skipStartAnimation && firstRender,
  });

  return (
    <Wrapper style={style}>
      <ProgressLabel
        style={{
          left: value.to(v => `${(v / max) * 100}%`),
          transform: value.to(v => `translateX(${(v / max) * -100}%)`),
        }}
      >
        <Score score={displayedValue} />
      </ProgressLabel>
      <Progress
        max={max}
        value={toValue}
        skipStartAnimation={skipStartAnimation}
      />
    </Wrapper>
  );
};
